// Collab-mode coordinator — a module SINGLETON (the teamHub / multiScreenService
// lifecycle pattern) so the presenter/follower role survives route changes:
// starting a session in the viewer, hopping to the worklist and back must not
// end the broadcast, and a follower arriving via a /viewer/{id}?collab= link
// enters follower mode before the viewer shell has even mounted.
//
// This module holds WHO we are in a session (role + ids); the live wire traffic
// (state broadcasts, pointer, join/leave) belongs to the viewer-side hooks
// (components/viewer/collab/*), which read this store to know what to do.
import { useSyncExternalStore } from 'react';
const IDLE = {
    role: null,
    sessionId: null,
    studyId: null,
    caseId: null,
    presenterName: null,
};
class CollabSessionStore {
    snapshot = IDLE;
    listeners = new Set();
    /** Stable reference while nothing changed (useSyncExternalStore contract). */
    getSnapshot = () => this.snapshot;
    subscribe = (listener) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };
    set(next) {
        const prev = this.snapshot;
        if (prev.role === next.role &&
            prev.sessionId === next.sessionId &&
            prev.studyId === next.studyId &&
            prev.caseId === next.caseId &&
            prev.presenterName === next.presenterName) {
            return;
        }
        this.snapshot = next;
        for (const listener of [...this.listeners]) {
            try {
                listener();
            }
            catch (err) {
                // A broken subscriber must not break delivery to the others.
                console.error('collabSession listener failed', err);
            }
        }
    }
    /** The local user started presenting (startCollabSession succeeded). */
    startPresenter(args) {
        this.set({
            role: 'presenter',
            sessionId: args.sessionId,
            studyId: args.studyId,
            caseId: args.caseId ?? null,
            presenterName: null,
        });
    }
    /**
     * The local user opened a ?collab= join link. Study/case ids resolve later
     * from the hub's joinCollab result (setFollowerJoinInfo). A presenter
     * clicking their OWN invite must not demote themselves to follower.
     */
    enterFollower(sessionId) {
        const current = this.snapshot;
        if (current.role === 'presenter' && current.sessionId === sessionId)
            return;
        if (current.role === 'follower' && current.sessionId === sessionId)
            return;
        this.set({
            role: 'follower',
            sessionId,
            studyId: null,
            caseId: null,
            presenterName: null,
        });
    }
    /** Fill in session details once joinCollab answers. Follower-only. */
    setFollowerJoinInfo(info) {
        const current = this.snapshot;
        if (current.role !== 'follower')
            return;
        this.set({ ...current, studyId: info.studyId, caseId: info.caseId });
    }
    setPresenterName(name) {
        const current = this.snapshot;
        if (current.role !== 'follower')
            return;
        this.set({ ...current, presenterName: name });
    }
    /** Leave collab mode (either role). Idempotent. */
    stop() {
        this.set(IDLE);
    }
    /**
     * Exit follower mode for ONE session — used when the viewer page that
     * entered follower mode unmounts, so following never latches: without this,
     * re-opening the same study later for one's own reading would silently
     * re-arm the follower and let the presenter drive the viewer. Never touches
     * presenter mode (a presenter's session survives navigation by design).
     */
    stopFollowerOf(sessionId) {
        const current = this.snapshot;
        if (current.role !== 'follower' || current.sessionId !== sessionId)
            return;
        this.set(IDLE);
    }
}
export const collabSession = new CollabSessionStore();
/** React subscription to the collab-session singleton. */
export function useCollabSessionSnapshot() {
    return useSyncExternalStore(collabSession.subscribe, collabSession.getSnapshot);
}
